// Money and dates on the purchasing screens: "€4,816.99", "22 Oct", "22 Oct, 16:40" (Albanian month names on the 'al' dashboard).
import { CURRENCY_SYMBOL } from '../list/listShared';

const MONEY = new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const QUANTITY = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 3 });

export const money = (value: number | string | null | undefined): string => {
  const n = Number(value);
  if (value === null || value === undefined || value === '' || !Number.isFinite(n)) return '—';
  return `${n < 0 ? '−' : ''}${CURRENCY_SYMBOL}${MONEY.format(Math.abs(n))}`;
};

export const quantity = (value: number | string | null | undefined): string => {
  const n = Number(value);
  return Number.isFinite(n) ? QUANTITY.format(n) : '—';
};

export const round2 = (value: number): number => Math.round((Number(value) || 0) * 100) / 100;

const dateLocale = () => (localStorage.getItem('language') === 'al' ? 'sq-AL' : 'en-GB');

const toDate = (value: unknown): Date | null => {
  if (value === null || value === undefined || value === '') return null;
  const date = new Date(value as string);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** "22 Oct" this year, "22 Oct 2025" otherwise. */
export const dayMonth = (value: unknown): string => {
  const date = toDate(value);
  if (!date) return '—';
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return new Intl.DateTimeFormat(dateLocale(), { day: 'numeric', month: 'short', year: sameYear ? undefined : 'numeric' }).format(date);
};

/** "22 Oct, 16:40". */
export const dayMonthTime = (value: unknown): string => {
  const date = toDate(value);
  if (!date) return '—';
  const time = new Intl.DateTimeFormat(dateLocale(), { hour: '2-digit', minute: '2-digit', hour12: false }).format(date);
  return `${dayMonth(date)}, ${time}`;
};

/** Local midnight `days` days before today (0 = today). */
export const startOfDay = (daysAgo = 0): Date => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - daysAgo);
  return date;
};

/** "2026-10-22" for an <input type="date"> in local time. */
export const toDateInput = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

/** Local midnight of an <input type="date"> value. */
export const fromDateInput = (value: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value ?? '');
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
};

/** "Thu 22 Oct 2026, 09:14" for the document facts. */
export const fullDateTime = (value: unknown): string => {
  const date = toDate(value);
  if (!date) return '—';
  const day = new Intl.DateTimeFormat(dateLocale(), { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }).format(date).replace(/,/g, '');
  const time = new Intl.DateTimeFormat(dateLocale(), { hour: '2-digit', minute: '2-digit', hour12: false }).format(date);
  return `${day}, ${time}`;
};

/** "Thu 22 Oct 2026"; `utc` reads the day in UTC. */
export const fullDate = (value: unknown, utc = false): string => {
  const date = toDate(value);
  if (!date) return '—';
  return new Intl.DateTimeFormat(dateLocale(), { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: utc ? 'UTC' : undefined })
    .format(date)
    .replace(/,/g, '');
};

/** A document date; older screens saved a bare day (UTC midnight), which shows as that day without a time. */
export const documentDateTime = (value: unknown): string =>
  typeof value === 'string' && /T00:00:00(\.000)?Z$/.test(value) ? fullDate(value, true) : fullDateTime(value);

/** "+€1.84" or "−€7.20": a stock impact, which can go either way. */
export const signedMoney = (value: number | string | null | undefined): string => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 && value !== null && value !== '' ? `+${money(n)}` : money(value);
};
